// ============================================================================
// AREA HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { areaService } from '../../api/services/areaService';
import type {
  Area,
  AreaListItem,
  CreateAreaRequest,
  UpdateAreaRequest,
  AreaSearchRequest,
} from '../../api/types/area.types';
import type { ID } from '../../api/types';

export const useAreas = (params?: AreaSearchRequest) => {
  const [data, setData] = useState<AreaListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchAreas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await areaService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchAreas(); }, [fetchAreas]);

  return { data, loading, error, pagination, refetch: fetchAreas };
};

export const useArea = (id: ID | undefined) => {
  const [data, setData] = useState<Area | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchArea = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await areaService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchArea(); }, [fetchArea]);

  return { data, loading, error, refetch: fetchArea };
};

export const useCreateArea = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createArea = async (
    data: CreateAreaRequest,
    options?: { onSuccess?: (area: Area) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await areaService.create(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { createArea, loading, error };
};

export const useUpdateArea = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateArea = async (
    id: ID, data: UpdateAreaRequest,
    options?: { onSuccess?: (area: Area) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await areaService.update(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { updateArea, loading, error };
};

export const useDeleteArea = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteArea = async (
    id: ID,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      await areaService.delete(id);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { deleteArea, loading, error };
};
